import router from '@/router'
import to from 'await-to-js'
import {
  reportLoadingOfProducts,
  reportPromoCardImpression,
} from '@/components/FilterPage/filter-helpers'
import { isRestoringScroll } from '@/components/FilterPage/scroll-restoration-helpers'
import { DY_PAGE_TYPES } from '@/services/external-communication/dynamic-yield/dy-events'
import {
  fetchPaginatedItems,
  fetchFilters,
  fetchPromoCards,
  fetchSubPages,
  fetchFilterPageData,
  formatSubpages,
  formatFilters,
  formatItems,
  addValuesToFilters,
  scrollToFiltersBar,
  newPageInfo,
} from './filter-store-helpers'
import {
  SORTING_FILTER,
  SORTING_OPTIONS,
  DEFAULT_ITEMS_PER_PAGE,
  PAGINATION_LAST_PAGE,
  PAGINATION_FILTER,
} from '@/components/FilterPage/constants'

export default {
  async initFilterPage({ dispatch, commit }, { route }) {
    /**
     * Fetch Filters options
     *   - Get filters/sorting from URL
     * Async Fetch
     *   - Products (filtered/sorted)
     *   - PromoCards (filtered)
     *   - SubPages (filtered)
     *   - TopBanner
     *   - Featured Articles
     * Subscribe to Mutations
     *   - Active Filters: Fetch Products/Promocards/SubPages
     *   - Active Sorting: Fetch Products
     */

    // Filters (sync)
    // 1. fetch
    commit('setFiltersFetched', false)
    const filtersData = await fetchFilters({ identifier: On.filterPage.identifier })
    commit('setFiltersFetched', true)
    // 2. format
    filtersData.filters = formatFilters(filtersData.filters)
    // 3. store
    commit('setFilters', filtersData.filters)
    // 4. activate from url and fetch data
    dispatch('refreshFilterPage', { route, isInit: true })
  },

  async refreshFilterPage(
    { dispatch, commit, state },
    { route, isInit = false, isNavigation = false }
  ) {
    // Reset
    commit('clearActiveFilters')
    commit('setPageInfo', newPageInfo())
    // Filters from route
    if (isNavigation) {
      // reset all previous filters, sort and pagination
      commit('setActiveFilters', [])
      commit('setActiveSort', SORTING_OPTIONS.featured)
      commit('setStartCursor', '')
    }
    dispatch('setFiltersFromRoute', route)
    // Async
    dispatch('getProducts', { before: state.pageInfo.startCursor, isInit, isRefreshing: true })
    dispatch('getPromoCards')
    dispatch('getSubPages')
    dispatch('getPageData')
  },

  async getProducts(
    { dispatch, commit, state, getters },
    { before = '', after = '', isInit = false, isRefreshing = false } = {}
  ) {
    // loading flags ON
    after ? commit('setFetchingMoreProducts', true) : commit('setProductsFetched', false)

    // scroll to 0 if initing page OR
    // scroll to bar if not fetching previous or next pages
    if (!isRestoringScroll()) {
      if (isRefreshing) window.scrollTo(0, 0)
      else if (!before && !after) scrollToFiltersBar()
    }

    // setup query
    const query = {
      identifier: On.filterPage.identifier,
      // ! if before is set we must fetch ALL items BEFORE this cursor
      first: before ? null : DEFAULT_ITEMS_PER_PAGE,
      // ! if before is 'last' we must fetch ALL items, so before is empty
      before: before === PAGINATION_LAST_PAGE ? null : before,
      after,
    }

    // sorting
    if (state.activeSort.code && state.activeSort.code !== SORTING_OPTIONS.featured.code)
      query.sortBy = {
        feature: state.activeSort.feature,
        order: state.activeSort.order,
      }

    // filters
    if (getters.activeItemFiltersQuery.length) query.itemFilters = getters.activeItemFiltersQuery
    if (getters.activeVariantFiltersQuery.length)
      query.variantFilters = getters.activeVariantFiltersQuery

    // 1. fetch
    const itemsData = await fetchPaginatedItems({ ...query })

    // loading flags OFF
    after ? commit('setFetchingMoreProducts', false) : commit('setProductsFetched', true)

    // 2. format (productGroup, variants, explodedVariants)
    itemsData.items = formatItems(itemsData.nodes, itemsData.explodeVariants)

    // 3. store - IF after is set, we only append new items
    const newItems = after ? [...state.products, ...itemsData.items] : itemsData.items
    commit('setProducts', newItems)
    commit('setHasExplodedVariants', itemsData.explodeVariants)
    commit('setHasArticlesPageBottom', itemsData.articlesPageBottom)
    commit('setPageInfo', { ...itemsData.pageInfo, totalCount: itemsData.totalCount }) // pagination

    // 4. track
    reportLoadingOfProducts(state.products)

    // 5. update URL
    if (!isInit) dispatch('updateURL')
  },

  async getPromoCards({ commit, getters }) {
    // 1. fetch
    commit('setPromoCardsFetched', false)
    const data = await fetchPromoCards({
      identifier: On.filterPage.identifier,
      filters: getters.activeItemFiltersQuery,
    })
    commit('setPromoCardsFetched', true)
    // 2. format
    data.promoCards.forEach(promo => (promo.isPromoCard = true))
    // 3. store
    commit('setPromoCards', data.promoCards)
    // 4. track
    reportPromoCardImpression(data.promoCards)
  },

  async getSubPages({ commit }) {
    // 1. fetch
    // ! We gonna filter subpages on the FE, should be moved to BE in future interations
    commit('setSubPagesFetched', false)
    const data = await fetchSubPages({ identifier: On.filterPage.identifier })
    commit('setSubPagesFetched', true)
    // 2. format
    data.subPages = formatSubpages(data.subPages)
    // 3. store
    commit('setSubPages', data.subPages)
  },

  async getPageData({ commit }) {
    // 1. fetch
    commit('setDataFetched', false)
    const data = await fetchFilterPageData({ identifier: On.filterPage.identifier })
    commit('setDataFetched', true)
    // 2. store
    commit('setTopBanner', data.topBanner)
    commit('setFeaturedArticles', data.featuredArticles)
  },

  setFiltersFromRoute({ commit, getters, state }, route) {
    /**
     * Context:
     * It seems too complex but it's necessary!
     * Currently URL parameters can follow any order, and some can be multiple:
     * - <url>/mens/shoes/support/active/road/trail?color=black%20white&size=10&sort=bestseller
     * - <url>/shoes/road/trail
     * So to be able to read and evaluate it we need to do some gymnastics
     */

    const filtersFromRoute = [] // [ { id: String, values: Array } ]
    let sortingFromRoute = null
    let cursorFromRoute = ''

    // <= $route.params.pathMatch = '/mens/shoes/invalid'
    //    => [['mens'], ['shoes'], ['invalid']] - Because filter values can be multiples
    // route.params.pathMatch is a String
    const pathMatches = route.params?.pathMatch?.length
      ? [...new Set(route.params.pathMatch.replace(/(^\/|\/$)/, '').split('/'))].map(p => [p])
      : []

    pathMatches.forEach(values => {
      // <= [['mens'], ['shoes'], ['invalid']]
      //    => [{id: 'gender', values: ['mens']}, {id: 'product', values: ['shoes']}]
      const validFilter = getters.pathFilters.find(filter =>
        filter.values.some(path => values.includes(path))
      )
      if (validFilter) addValuesToFilters(filtersFromRoute, validFilter, values)
    })

    // <= route.query: {color: 'black white', invalid: 'foo', sort: 'bestseller desc'}
    //    => [{id: 'color', values: ['black', 'white']}]
    //    => { feature: 'bestseller', order: 'desc' }
    Object.keys(route.query).forEach(id => {
      // 'black+white' || 'black white' || 'black%20white' => ['black', 'white']
      const values = route.query[id].split(/\s|\+\|%20|%2b/i)

      if (id === SORTING_FILTER) {
        // sorting
        sortingFromRoute = SORTING_OPTIONS[values[0]]
      } else if (id === PAGINATION_FILTER) {
        // pagination cursor
        cursorFromRoute = values[0]
      } else {
        // filter
        const validFilter = state.filters.find(filter => filter.id === id)
        if (validFilter) addValuesToFilters(filtersFromRoute, validFilter, values)
      }
    })

    if (filtersFromRoute.length) commit('setActiveFilters', filtersFromRoute)
    if (sortingFromRoute) commit('setActiveSort', sortingFromRoute)
    if (cursorFromRoute) commit('setStartCursor', cursorFromRoute)
  },

  setPrimaryFilter({ dispatch, getters, commit }, newFilter) {
    commit('setActiveFilters', [newFilter])
    commit('syncActiveFilters', getters.visibleSecondaryFilters)
    // fetch new results
    dispatch('getProducts')
    dispatch('getPromoCards')
  },

  removePrimaryFilter({ dispatch, getters, commit }, oldFilter) {
    commit('removeActiveFilterValue', oldFilter)
    commit('syncActiveFilters', getters.visibleSecondaryFilters)
    // fetch new results
    dispatch('getProducts')
    dispatch('getPromoCards')
  },

  setSecondaryFilter({ dispatch, commit }, newFilter) {
    commit('setActiveFilters', [newFilter])
    // fetch new results
    dispatch('getProducts')
    dispatch('getPromoCards')
  },

  removeSecondaryFilter({ dispatch, commit }, oldFilter) {
    commit('removeActiveFilterValue', oldFilter)
    // fetch new results
    dispatch('getProducts')
    dispatch('getPromoCards')
  },

  clearFilters({ dispatch, commit }) {
    commit('clearActiveFilters')
    // fetch new results
    dispatch('getProducts')
    dispatch('getPromoCards')
  },

  clearSecondaryFilters({ dispatch, commit }) {
    commit('clearActiveSecondaryFilters')
    // fetch new results
    dispatch('getProducts')
    dispatch('getPromoCards')
  },

  setSorting({ dispatch, commit }, sorting) {
    commit('setActiveSort', sorting)
    // fetch new results
    dispatch('getProducts')
    dispatch('getPromoCards')
  },

  async updateURL({ commit, state, getters }) {
    // $route.param = :gender?/:type?/[:pathFilters]?
    const pathMatch = []
    if (getters.activeGender) pathMatch.push(getters.activeGender)
    if (getters.activeProductType) pathMatch.push(getters.activeProductType)
    pathMatch.push(...getters.paramsFilters.map(filter => filter.values).flat())
    const path = `/${On.filterPage.identifier}/${pathMatch.join('/')}`

    // $route.query = { id: values }
    const query = getters.queryFilters.reduce((query, filter) => {
      query[filter.id] = filter.values.join('+')
      return query
    }, {})

    // sort
    if (state.activeSort.code && state.activeSort.code !== SORTING_OPTIONS.featured.code)
      query[SORTING_FILTER] = state.activeSort.code

    // pagination
    // IF on the last page, add a 'last' cursor
    // ELSE IF not on the first page, add the endCursor
    if (!state.pageInfo.hasNextPage) query.page = PAGINATION_LAST_PAGE
    else if (state.pageInfo.hasPreviousPage) query.page = state.pageInfo.endCursor

    // update meta data
    router.currentRoute.meta.metaTitle = getters.activeSubPage?.metaTitle
    router.currentRoute.meta.metaDescription = getters.activeSubPage?.metaDescription

    /**
     * During the router update cycle, our beforeRouteUpdate guard will be triggered.
     * This flag avoids calling actions twice.
     */
    commit('setUpdatedByFilter', true)
    await to(router.push({ path, query }))
    commit('setUpdatedByFilter', false)

    const notifier = this._vm.$notifier
    notifier.trackEvent({
      action: notifier.ACTIONS.virtPath,
      details: {
        virtPagePath: path,
        virtPageTitle: document.title,
        dyPageType: DY_PAGE_TYPES.category,
        dyPageData: getters.activePrimaryFilters,
      },
    })
  },
}
